'use client';

import { LucideIcon } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import LoadingSpinner from '@/components/ui/loristry-spinner';
import { cn } from '@/lib/utils';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  variant?: 'primary' | 'secondary' | 'text';
  isLoading?: boolean;
}

interface EmptyStateProps {
  /** Icon component to display */
  icon: LucideIcon;
  /** Title text */
  title: string;
  /** Description text */
  description: string;
  /** Primary action button */
  action?: EmptyStateAction;
  /** Secondary action button */
  secondaryAction?: EmptyStateAction;
  /** Whether to use destructive styling (for error states) */
  destructive?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
  /** Additional CSS classes for the content wrapper */
  contentClassName?: string;
}

/**
 * Empty state component
 *
 * Displays a consistent empty state with icon, title, description, and optional actions.
 * Follows the design system pattern with colored icon circle, bold title, and centered layout.
 *
 * @example Basic empty state
 * ```tsx
 * <EmptyState
 *   icon={Quote}
 *   title="No Stories Yet"
 *   description="Start a conversation to create your first story."
 *   action={{
 *     label: "Start Conversation",
 *     onClick: () => router.push('/conversation'),
 *     icon: Mic
 *   }}
 * />
 * ```
 *
 * @example Multiple actions
 * ```tsx
 * <EmptyState
 *   icon={Quote}
 *   title="No Stories Yet"
 *   description="Share this board with others to start building your story archive."
 *   action={{
 *     label: "Invite Your Family",
 *     onClick: () => setShowInviteModal(true),
 *     icon: Users
 *   }}
 *   secondaryAction={{
 *     label: "Start Conversation",
 *     onClick: () => router.push('/conversation'),
 *     icon: Mic,
 *     variant: "secondary"
 *   }}
 * />
 * ```
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  destructive = false,
  className,
  contentClassName,
}: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center', 'p-12', className)}>
      <div className={cn('text-center space-y-6', contentClassName)}>
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div
            data-testid="empty-state-icon"
            className={cn('rounded-full p-4', destructive && 'bg-destructive/10')}
            style={!destructive ? { backgroundColor: 'var(--primary-40)' } : undefined}
          >
            <Icon
              className={cn('h-8 w-8', destructive && 'text-destructive')}
              style={!destructive ? { color: 'var(--primary-120)' } : undefined}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Title and Description */}
        <div className="space-y-2">
          <h2
            className={cn(destructive ? 'text-xl' : 'text-lg')}
            style={{ color: 'var(--text-primary)' }}
          >
            {title}
          </h2>
          <p
            className="text-sm max-w-md mx-auto text-pretty"
            style={{ color: 'var(--text-secondary)' }}
          >
            {description}
          </p>
        </div>

        {/* Actions */}
        {(action || secondaryAction) && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {action && (
              <Button
                variant={action.variant ?? 'primary'}
                size="lg"
                onClick={action.onClick}
                className={cn('gap-2', secondaryAction ? 'w-full sm:w-auto' : '')}
                // Coerce undefined → false to satisfy exactOptionalPropertyTypes in pre-push strict
                // tsc (MEMORY.md: exact-optional applies). Behaves identically at runtime.
                disabled={action.isLoading ?? false}
              >
                {action.isLoading && <LoadingSpinner size={16} text="" />}
                {action.icon && <action.icon className="w-5 h-5" />}
                {action.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant={secondaryAction.variant ?? 'secondary'}
                size="lg"
                onClick={secondaryAction.onClick}
                className="gap-2 w-full sm:w-auto"
                // Coerce undefined → false to satisfy exactOptionalPropertyTypes in pre-push strict
                // tsc (MEMORY.md: exact-optional applies). Behaves identically at runtime.
                disabled={secondaryAction.isLoading ?? false}
              >
                {secondaryAction.isLoading && <LoadingSpinner size={16} text="" />}
                {secondaryAction.icon && <secondaryAction.icon className="w-5 h-5" />}
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
